import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Alert,
  alpha,
  Box,
  Button,
  Fab,
  Typography,
  useTheme,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Grid from "@mui/material/Grid";
import HeaderWithButton from "../../components/Header";
import { useCompanyContext } from "../../contexts/CompanyContext";
import DocumentsView from "../../components/DocumentsView";
import DocumentsUpload from "../../components/DocumentsUpload";
import ConfirmationDialog from "../../components/ConfirmationDialog";

import { Icon } from "@iconify/react";
import StyledSelect from "../../components/Fields/SingleSelect";
import { useNavigate, useLocation } from "react-router-dom";
import RequestSpecsForm from "./components/RequestSpecsForm";
import AIInsightForm from "./components/AIInsightForm";
import CompanyStandardForm from "./components/CompanyStandardForm";
import DocumentContradictionForm from "./components/DocumentContradictionForm";
import type { DocumentSlot, UploadedFile } from "./types";
import {
  docKindFor,
  displayFileName,
  FileTypeIcon,
  Icon as UiIcon,
} from "../../components/ui/icons";
import { formatSizeInMB, formatDate } from "../../utils/formatUtils";
import { buildBlobProxyUrl } from "../../utils/blobProxyUrl";
import {
  type CreateUserDocumentDto,
  type FileUploadRequestDto,
  type WorkflowDocumentDto,
  type FileUploadUrlDto,
  fileToBase64,
  getContentType,
  userDocumentApi,
} from "../../services/apiService";
import {
  userDocumentService,
  type UserDocumentResponse,
  type UpdateUserDocumentDto,
  type CreateDocumentContradictionDto,
  type WorkflowResponseDto,
  type JobStatusResponseDto,
} from "../../services";
import {
  configurationService,
  type CompanyRuleDto,
} from "../../services/configurationService";
import { useAuth } from "../../hooks/useAuth";
import { useCompany } from "../../hooks/useCompany";
import { usePermissions } from "../../hooks/usePermissions";
import BDrawer from "../../components/BDrawer";
import { useCurrentUserId } from "../../hooks/useCurrentUserId";
import { set } from "lodash";

// Types are now in ./types.ts

const Functionaliteiten: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { currentAccount, isAuthenticated } = useAuth();
  const { canCreate, canRead } = usePermissions();
  const {
    isSuperAdmin,
    selectedCompanyId: autoSelectedCompanyId,
  } = useCompany();
  const { selectedCompanyId: contextCompanyId } = useCompanyContext();

  // Module selection
  const [selectedModule, setSelectedModule] = useState<string>("");

  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  // Get screen name based on selected module
  const getScreenName = () => {
    switch (selectedModule) {
      case "Request Specs":
        return "SpecsComparison";
      case "Document Contradiction":
        return "DocumentContradiction";
      case "AI Insight":
        return "AIInsights";
      case "Company Standard Document":
        return "CompanyStandardDocument";
      default:
        return "";
    }
  };
  
  const SCREEN_NAME = getScreenName();
  const canCreateRequestSpecs = canCreate("SpecsComparison");
  const canCreateDocumentContradiction = canCreate("DocumentContradiction");
  const canCreateAIInsight = canCreate("AIInsights");
  const canCreateCompanyStandard = canCreate("Configuration");

  // File type validation helper - only modern formats are allowed
  const VALID_FILE_TYPES = [".pdf", ".docx"];
  const FILE_TYPE_ERROR = t("functionalities.page.invalidFileType");

  const isValidFileType = (fileName: string): boolean => {
    const lowerName = fileName.toLowerCase();
    return VALID_FILE_TYPES.some((type) => lowerName.endsWith(type));
  };
  
  const [bottomDrawerOpen, setBottomDrawerOpen] = useState<boolean>(false);
  
  // Request Specs state
  const [comparisonName, setComparisonName] = useState("");
  const [comparisonType, setComparisonType] = useState("Original Comparison");
  const [document1, setDocument1] = useState<DocumentSlot>(null);
  const [document2, setDocument2] = useState<DocumentSlot>(null);
  
  // Document Contradiction state
  const [contradictionName, setContradictionName] = useState("");
  const [contradictionReferenceDocument, setContradictionReferenceDocument] = useState<DocumentSlot>(null);
  const [contradictionTargetDocument, setContradictionTargetDocument] = useState<DocumentSlot>(null);
  
  // AI Insight state
  const [multipleFiles, setMultipleFiles] = useState<File[]>([]);
  const [multipleSelectedDocuments, setMultipleSelectedDocuments] = useState<
    UserDocumentResponse[]
  >([]);
  const [analysisType, setAnalysisType] = useState<string>("");
  const [customInstructions, setCustomInstructions] = useState<string>("");
  
  // Job polling state
  const [pollingJobId, setPollingJobId] = useState<string | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Company Standard Document state
  const [companyRules, setCompanyRules] = useState<CompanyRuleDto[]>([]);
  const [selectedRuleIds, setSelectedRuleIds] = useState<string[]>([]);
  const [rulesLoading, setRulesLoading] = useState<boolean>(false);
  const [noRulesError, setNoRulesError] = useState<string | null>(null);
  
  // Common state
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const errorRef = useRef<HTMLDivElement>(null);

  const isSmallScreen = useMediaQuery(theme.breakpoints.down("lg"));

    // Confirmation dialog state
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<{
    open: boolean;
    documentToDelete: UserDocumentResponse | null;
  }>({
    open: false,
    documentToDelete: null,
  });

  // Preselect module from navigation state
  useEffect(() => {
    const incoming = (location.state as { selectedModule?: string } | undefined)?.selectedModule;
    if (incoming) {
      setSelectedModule(incoming);
    } else {
      setSelectedModule("Request Specs");
    }
  }, [location.state]);
  
  // Use company from context (header dropdown) for super admin, otherwise use hook value
  const selectedCompanyId = isSuperAdmin && contextCompanyId && contextCompanyId !== "all" 
    ? contextCompanyId 
    : autoSelectedCompanyId;

  // Scroll to error when it appears
  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [error]);

  // Reset form state when module changes
  useEffect(() => {
    // Reset all form states when switching modules
    setError(null);
    setProcessingError(null);
    setRetryCount(0);
    setIsProcessing(false);
    setIsLoading(false);
    
    // Reset module-specific states
    if (selectedModule === "Request Specs") {
      setComparisonName("");
      setComparisonType("Original Comparison");
      setDocument1(null);
      setDocument2(null);
    } else if (selectedModule === "Document Contradiction") {
      setContradictionName("");
      setContradictionReferenceDocument(null);
      setContradictionTargetDocument(null);
    } else if (selectedModule === "AI Insight") {
      setAnalysisType("");
      setCustomInstructions("");
      setMultipleFiles([]);
      setMultipleSelectedDocuments([]);
    } else if (selectedModule === "Company Standard Document") {
      setCustomInstructions("");
      setMultipleFiles([]);
      setMultipleSelectedDocuments([]);
      setSelectedRuleIds([]);
    }
  }, [selectedModule]);

  // Fetch company rules when company changes (for Company Standard Document)
  useEffect(() => {
    const fetchCompanyRules = async () => {
      if (selectedCompanyId && selectedModule === "Company Standard Document") {
        setRulesLoading(true);
        setNoRulesError(null);
        try {
          const rules = await configurationService.getCompanyRules(
            selectedCompanyId
          );
          const activeRules = rules.filter((rule) => rule.isActive);
          setCompanyRules(activeRules);
          setSelectedRuleIds([]);

          if (activeRules.length === 0) {
            setNoRulesError(
              t("functionalities.page.noRulesConfigured")
            );
          }
        } catch (error) {
          console.error("Failed to fetch company rules:", error);
          setCompanyRules([]);
          setNoRulesError(
            t("functionalities.page.rulesLoadError")
          );
        } finally {
          setRulesLoading(false);
        }
      } else {
        setCompanyRules([]);
        setSelectedRuleIds([]);
        setNoRulesError(null);
      }
    };

    fetchCompanyRules();
  }, [selectedCompanyId, selectedModule]);

  // Processing states
  const [processingTime, setProcessingTime] = useState<number>(0);
  const [retryCount, setRetryCount] = useState<number>(0);
  const [processingError, setProcessingError] = useState<string | null>(null);

  // Timer ref
  const processingTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Session storage keys
  const ANALYSIS_RESULT_KEY =
    process.env.REACT_APP_ANALYSIS_RESULT_STORAGE_KEY ||
    "documentAnalysisResult";
  const COMPANY_STANDARD_DOCUMENTS_KEY =
    process.env.REACT_APP_COMPANY_STANDARD_DOCUMENTS_STORAGE_KEY ||
    "companyStandardDocuments";
  const CONTRADICTION_RESULT_KEY =
    process.env.REACT_APP_CONTRADICTION_RESULT_STORAGE_KEY ||
    "contradictionResult";

  // Timer effect
  useEffect(() => {
    if (isProcessing) {
      setProcessingTime(0);
      processingTimerRef.current = setInterval(() => {
        setProcessingTime((prev) => prev + 1);
      }, 1000);
    } else {
      if (processingTimerRef.current) {
        clearInterval(processingTimerRef.current);
        processingTimerRef.current = null;
      }
    }

    return () => {
      if (processingTimerRef.current) {
        clearInterval(processingTimerRef.current);
      }
    };
  }, [isProcessing]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []);

  // Format time helper
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  // Retry processing
  const handleRetryProcessing = () => {
    setRetryCount((prev) => prev + 1);
    setProcessingError(null);
    if (selectedModule === "Request Specs" || (selectedModule === "Request Specs" && comparisonType === "Content Comparison")) {
      handleComparisonButtonClick();
    } else if (selectedModule === "Document Contradiction") {
      handleDocumentContradictionButtonClick();
    } else if (selectedModule === "AI Insight") {
      handleAIInsightButtonClick();
    } else if (selectedModule === "Company Standard Document") {
      handleCompanyStandardButtonClick();
    }
  };

  const handleOpenBottomDrawer = () => {
    setBottomDrawerOpen(true);
  };

  const handleCloseBottomDrawer = () => {
    setBottomDrawerOpen(false);
  };
  
    const handleComparisonButtonClick = async () => {
    // If Content Comparison is selected, use Document Contradiction handler
    if (comparisonType === "Content Comparison") {
      if (!canCreateDocumentContradiction) {
        setError(
          t("functionalities.page.noComparisonPermission")
        );
        return;
      }

      if (!document1 || !document2 || !comparisonName) {
        setError(t("functionalities.page.fillFieldsComparison"));
        return;
      }

      // Company validation for super admin users
      if (isSuperAdmin && (!contextCompanyId || contextCompanyId === "all")) {
        setError(t("functionalities.page.selectCompanyFromDropdown"));
        return;
      }

      if (!isAuthenticated || !currentAccount) {
        setError(t("requestSpecs.loginRequired"));
        return;
      }

      try {
        setIsProcessing(true);
        setError(null);
        setProcessingError(null);

        const userId = currentAccount.username;
        const companyId = selectedCompanyId || undefined;

        // Process reference document (document1)
        let referenceFile: FileUploadRequestDto | undefined;
        let referenceFileUrl: string | undefined;

        if ("file" in document1 && document1.file) {
          const base64File = await fileToBase64(document1.file);
          referenceFile = {
            base64File,
            fileName: document1.file.name,
            contentType: getContentType(document1.file.name),
          };
        } else {
          const existingDoc = document1 as UserDocumentResponse;
          referenceFileUrl = existingDoc.documentLink;
        }

        // Process target document (document2)
        let targetFile: FileUploadRequestDto | undefined;
        let targetFileUrl: string | undefined;

        if ("file" in document2 && document2.file) {
          const base64File = await fileToBase64(document2.file);
          targetFile = {
            base64File,
            fileName: document2.file.name,
            contentType: getContentType(document2.file.name),
          };
        } else {
          const existingDoc = document2 as UserDocumentResponse;
          targetFileUrl = existingDoc.documentLink;
        }

        // Prepare API request
        const createContradictionRequest: CreateDocumentContradictionDto = {
          userId,
          companyId,
          title: comparisonName,
          workflowType: "documentcontradiction",
          description: "Document contradiction analysis",
          referenceFile,
          referenceFileUrl,
          targetFile,
          targetFileUrl,
        };

        // Make API call
        const result: WorkflowResponseDto = await userDocumentApi.createDocumentContradiction(
          createContradictionRequest
        );

        // Check the status
        if (result.status === "processing" && result.job_id) {
          const jobId = result.job_id;
          
          // Clear any existing interval first
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          
          // Start polling
          setPollingJobId(jobId);
          
          // Use a temporary marker to track if we should continue
          const tempMarker = {};
          pollingIntervalRef.current = tempMarker as any;
          
          // Poll immediately first time
          await pollJobStatus(jobId);
          
          // Only set up interval if polling wasn't stopped by the first call
          if (pollingIntervalRef.current !== tempMarker) {
            // First poll stopped polling, not setting up interval
          } else {
            // Then poll every 10 seconds
            pollingIntervalRef.current = setInterval(() => {
              pollJobStatus(jobId);
            }, 10000);
          }
          
        } else if (result.status === "completed") {
          // Immediate completion
          setIsProcessing(false);
          
          const contradictionData = {
            documentId: result.job_id || "generated-contradiction",
            fileName: comparisonName,
            analysisResults: result.data,
            referenceDocumentUrl: buildBlobProxyUrl(referenceFileUrl || ""),
            targetDocumentUrl: buildBlobProxyUrl(targetFileUrl || ""),
            timestamp: new Date().toISOString(),
          };
          
          sessionStorage.setItem(
            CONTRADICTION_RESULT_KEY,
            JSON.stringify(contradictionData)
          );
          
          navigate("/document-contradiction", {
            state: contradictionData,
          });
        } else if (result.status === "failed") {
          setIsProcessing(false);
          setProcessingError(
            result.message || t("functionalities.page.contradictionFailed")
          );
        }
      } catch (err) {
        console.error("Error creating contradiction analysis:", err);
        setIsProcessing(false);
        setProcessingError(
          err instanceof Error ? err.message : "Er is een fout opgetreden tijdens het verwerken"
        );
      }
      return;
    }

    // Original Comparison logic
    if (!canCreateRequestSpecs) {
      setError(t("functionalities.page.noComparisonPermission"));
      return;
    }

    if (!document1 || !document2 || !comparisonName) {
      setError(t("functionalities.page.fillFieldsComparison"));
      return;
    }

    // Company validation for super admin users
    if (isSuperAdmin && (!contextCompanyId || contextCompanyId === "all")) {
      setError(t("functionalities.page.selectCompanyFromDropdown"));
      return;
    }

    if (!isAuthenticated || !currentAccount) {
      setError(t("requestSpecs.loginRequired"));
      return;
    }

    try {
      setIsProcessing(true);
      setError(null);

      // Separate new files and existing documents
      const newFiles: FileUploadRequestDto[] = [];
      const existingFiles: WorkflowDocumentDto[] = [];

      // Process document1
      if ("file" in document1) {
        // It's a new uploaded file - use the actual file name from the File object
        const base64File = await fileToBase64(document1.file!);
        newFiles.push({
          base64File,
          fileName: `Vergelijk - ${document1.file!.name}`, // Use actual file name from File object
          contentType: getContentType(document1.file!.name),
        });
      } else {
        // It's an existing document (UserDocumentResponse)
        const existingDoc = document1 as UserDocumentResponse;
        existingFiles.push({
          url: existingDoc.documentLink,
          name: existingDoc.tittle,
          size: existingDoc.documentSize?.toString() || t("common.unknownSize"),
          mimeType: getContentType(existingDoc.tittle),
        });
      }

      // Process document2
      if ("file" in document2) {
        // It's a new uploaded file - use the actual file name from the File object
        const base64File = await fileToBase64(document2.file!);
        newFiles.push({
          base64File,
          fileName: `Vergelijk - ${document2.file!.name}`, // Use actual file name from File object
          contentType: getContentType(document2.file!.name),
        });
      } else {
        // It's an existing document (UserDocumentResponse)
        const existingDoc = document2 as UserDocumentResponse;
        existingFiles.push({
          url: existingDoc.documentLink,
          name: existingDoc.tittle,
          size: existingDoc.documentSize?.toString() || t("common.unknownSize"),
          mimeType: getContentType(existingDoc.tittle),
        });
      }

      // Prepare document creation request
      const createDocumentRequest: CreateUserDocumentDto = {
        userId: currentAccount.username,
        tittle: `Comparison - ${comparisonName}`,
        description: `Document contradiction between two specifications`,
        workflowType:
          comparisonType === "AI Comparison" ? "aicomparison" : "comparison",
        files: newFiles,
        exsitingFiles: existingFiles.length > 0 ? existingFiles : undefined,
        typeOfDocument: "CompareSpecs",
        companyId: selectedCompanyId || undefined,
        companyName: undefined,
      };

      // Make API call
      const result = await userDocumentApi.create(createDocumentRequest);

      // Check if we got the expected response format with word and pdf URLs
      if (
        result.success &&
        result.data &&
        typeof result.data === "object" &&
        "word" in result.data &&
        "pdf" in result.data
      ) {
        const responseData = result.data as { word: string; pdf: string };

        // Store in session storage for persistence on refresh
        const COMPARISON_RESULT_KEY =
          process.env.REACT_APP_COMPARISON_RESULT_STORAGE_KEY ||
          "specsComparisonResult";
        const comparisonData = {
          requestId: result.data.toString(),
          comparisonName,
          comparisonType,
          documentUrls: {
            word: responseData.word,
            pdf: responseData.pdf,
          },
          response: result,
          timestamp: new Date().toISOString(),
        };
        sessionStorage.setItem(
          COMPARISON_RESULT_KEY,
          JSON.stringify(comparisonData)
        );

        // Navigate to comparison results with the document URLs
        navigate("/specs-comparison", {
          state: comparisonData,
        });
      } else {
        // Fallback for other response formats
        const COMPARISON_RESULT_KEY =
          process.env.REACT_APP_COMPARISON_RESULT_STORAGE_KEY ||
          "specsComparisonResult";
        const comparisonData = {
          requestId: "generated-comparison",
          comparisonName,
          comparisonType,
          response: result,
          timestamp: new Date().toISOString(),
        };
        sessionStorage.setItem(
          COMPARISON_RESULT_KEY,
          JSON.stringify(comparisonData)
        );

        navigate("/specs-comparison", {
          state: comparisonData,
        });
      }
    } catch (err) {
      console.error("Error creating comparison document:", err);
      setProcessingError(
        err instanceof Error ? err.message : t("requestSpecs.processingError")
      );
    } finally {
      setIsProcessing(false);
    }
  };

  // File upload handling
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];

      // Validate file type
      if (!isValidFileType(file.name)) {
        setError(FILE_TYPE_ERROR);
        event.target.value = "";
        return;
      }

      // Validate file size (20MB limit)
      const maxSizeInMB = 20;
      const maxSizeInBytes = maxSizeInMB * 1024 * 1024;

      if (file.size > maxSizeInBytes) {
        setError(
          `Bestand "${file.name}" is te groot (${(
            file.size /
            1024 /
            1024
          ).toFixed(2)}MB). Maximum toegestane grootte is ${maxSizeInMB}MB.`
        );
        // Reset the file input
        event.target.value = "";
        return;
      }

      // Clear any existing errors
      setError(null);

      const uploadedFile: UploadedFile = {
        file,
        name: file.name,
        size: `${(file.size / 1024 / 1024).toFixed(1)} MB`,
        type: file.type,
        uploading: false,
        uploadProgress: 100,
      };

      // Determine which position based on the input ID
      const inputId = event.target.id;
      if (inputId === "file-upload-input-1") {
        setDocument1(uploadedFile);
      } else if (inputId === "file-upload-input-2") {
        setDocument2(uploadedFile);
      } else if (inputId === "file-upload-input-contradiction-1") {
        setContradictionReferenceDocument(uploadedFile);
      } else if (inputId === "file-upload-input-contradiction-2") {
        setContradictionTargetDocument(uploadedFile);
      }
    }
  };

  const handleRemoveFile = (position: 1 | 2, isContradiction: boolean = false) => {
    if (isContradiction) {
      if (position === 1) {
        setContradictionReferenceDocument(null);
      } else {
        setContradictionTargetDocument(null);
      }
    } else {
      if (position === 1) {
        setDocument1(null);
      } else {
        setDocument2(null);
      }
    }
  };

  const handleDocumentSelect = (
    selectedDoc: UserDocumentResponse,
    position: 1 | 2,
    isContradiction: boolean = false
  ) => {
    // Validate file type
    const docName = selectedDoc.tittle || selectedDoc.documentLink || "";
    if (!isValidFileType(docName)) {
      setError(FILE_TYPE_ERROR);
      return;
    }

    if (isContradiction) {
      if (position === 1) {
        setContradictionReferenceDocument(selectedDoc);
      } else {
        setContradictionTargetDocument(selectedDoc);
      }
    } else {
      if (position === 1) {
        setDocument1(selectedDoc);
      } else {
        setDocument2(selectedDoc);
      }
    }
  };

  const handleDownloadDocument = async (selectedDoc: UserDocumentResponse) => {
    try {
      if (!selectedDoc.documentLink) {
        console.error("No document link available");
        setError(t("requestSpecs.documentLinkNotAvailable"));
        return;
      }

      const response = await fetch(selectedDoc.documentLink, {
        method: "GET",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      // Extract filename from documentLink or use a default name
      const filename = selectedDoc.tittle || "document.pdf";
      link.download = filename;

      // Set target to blank to handle CORS issues with some blob storage
      link.target = "_blank";
      link.rel = "noopener noreferrer";

      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      console.log("Download initiated for:", selectedDoc.tittle);
    } catch (error) {
      console.error("Error downloading document:", error);
      setError(t("requestSpecs.downloadError"));
    }
  };

    const handleDocumentRemove = async (docToRemove: UserDocumentResponse) => {
    setDeleteConfirmDialog({
      open: true,
      documentToDelete: docToRemove,
    });
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirmDialog.documentToDelete) return;

    try {
      await userDocumentApi.delete(deleteConfirmDialog.documentToDelete.id);
      setDeleteConfirmDialog({ open: false, documentToDelete: null });
      toast.success(t("success.deleted"));
      // Refresh documents list
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error("Error removing document:", error);
      setError(t("requestSpecs.deleteError"));
      setDeleteConfirmDialog({ open: false, documentToDelete: null });
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmDialog({ open: false, documentToDelete: null });
  };

  // AI Insight handlers
  const handleMultipleFileUpload = (uploadedFiles: FileList) => {
    const filesArray = Array.from(uploadedFiles);
    const currentTotalFiles =
      multipleFiles.length + multipleSelectedDocuments.length;
    const maxFiles = 5;

    // Validate file types
    const invalidFiles = filesArray.filter((file) => !isValidFileType(file.name));

    if (invalidFiles.length > 0) {
      setError(
        `${FILE_TYPE_ERROR} ${t("functionalities.page.invalidFilesList", {
          files: invalidFiles.map((f) => f.name).join(", "),
        })}`
      );
      return;
    }

    if (currentTotalFiles + filesArray.length > maxFiles) {
      setError(
        t("functionalities.page.maxFilesSelectError", { max: maxFiles })
      );
      return;
    }

    setMultipleFiles((prev) => [...prev, ...filesArray]);
    // if (filesArray.length > 0) {
    //   setMultipleSelectedDocuments([]);
    // }
  };

  const handleMultipleFileRemove = (fileIndex: number) => {
    setMultipleFiles((prev) => prev.filter((_, index) => index !== fileIndex));
  };

  const handleMultipleDocumentSelect = (selectedDoc: UserDocumentResponse) => {
    // Validate file type
    const docName = selectedDoc.tittle || selectedDoc.documentLink || "";
    if (!isValidFileType(docName)) {
      setError(FILE_TYPE_ERROR);
      return;
    }

    const currentTotalDocuments =
      multipleFiles.length + multipleSelectedDocuments.length;
    const maxFiles = 5;

    if (multipleSelectedDocuments.some((doc) => doc.id === selectedDoc.id)) {
      return;
    }

    if (currentTotalDocuments >= maxFiles) {
      setError(
        t("functionalities.page.maxFilesSelectError", { max: maxFiles })
      );
      return;
    }

    setMultipleSelectedDocuments((prev) => [...prev, selectedDoc]);
    // if (multipleSelectedDocuments.length === 0) {
    //   setMultipleFiles([]);
    // }
  };

  const handleMultipleDocumentDeselect = (documentId: string) => {
    setMultipleSelectedDocuments((prev) =>
      prev.filter((doc) => doc.id !== documentId)
    );
    handleCloseBottomDrawer();
  };

  const handleClearAllMultipleSelections = () => {
    setMultipleFiles([]);
    setMultipleSelectedDocuments([]);
  };
  const userId = useCurrentUserId();
  const [isButtonsLoading, setIsButtonsLoading] = useState(false);
  const canRefreshDocuments = canRead(SCREEN_NAME);
 
  // Add this state at the top of your component
const [dragOverSlot, setDragOverSlot] = useState<{
  slotId: string;
  isDragging: boolean;
} | null>(null);

// Update the renderDocumentSlot function:
const renderDocumentSlot = (
  document: UploadedFile | UserDocumentResponse | null,
  label: string,
  inputId: string,
  canUpload: boolean,
  onRemove: () => void
) => {
  const isDragOver = dragOverSlot?.slotId === inputId && dragOverSlot?.isDragging;
  // The slot lives on the navy config panel on desktop: `text.disabled` there is
  // unreadable (UI/UX audit 2.4.1), so use a light ink instead.
  const dropzoneTextColor = isSmallScreen
    ? theme.palette.text.secondary
    : alpha(theme.palette.common.white, 0.7);
  const noAccessColor = isSmallScreen
    ? theme.palette.status.failFg
    : theme.palette.error.light;

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (canUpload && !isProcessing) {
      setDragOverSlot({ slotId: inputId, isDragging: true });
    }
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (canUpload && !isProcessing) {
      setDragOverSlot({ slotId: inputId, isDragging: true });
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    const relatedTarget = e.relatedTarget as Node;
    const currentTarget = e.currentTarget;
    
    if (!currentTarget.contains(relatedTarget)) {
      setDragOverSlot(null);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverSlot(null);

    if (canUpload && !isProcessing && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      
      // Validate file type
      if (!isValidFileType(file.name)) {
        setError(FILE_TYPE_ERROR);
        return;
      }

      // Validate file size (20MB limit)
      const maxSizeInMB = 20;
      const maxSizeInBytes = maxSizeInMB * 1024 * 1024;

      if (file.size > maxSizeInBytes) {
        setError(
          `Bestand "${file.name}" is te groot (${(
            file.size /
            1024 /
            1024
          ).toFixed(2)}MB). Maximum toegestane grootte is ${maxSizeInMB}MB.`
        );
        return;
      }

      // Clear any existing errors
      setError(null);

      const uploadedFile: UploadedFile = {
        file,
        name: file.name,
        size: `${(file.size / 1024 / 1024).toFixed(1)} MB`,
        type: file.type,
        uploading: false,
        uploadProgress: 100,
      };

      // Determine which position based on the input ID
      if (inputId === "file-upload-input-1") {
        setDocument1(uploadedFile);
      } else if (inputId === "file-upload-input-2") {
        setDocument2(uploadedFile);
      } else if (inputId === "file-upload-input-contradiction-1") {
        setContradictionReferenceDocument(uploadedFile);
      } else if (inputId === "file-upload-input-contradiction-2") {
        setContradictionTargetDocument(uploadedFile);
      }
    }
  };

  return (
    <Box mb={3}>
      <Typography
        variant="body1"
        sx={{
          color: isSmallScreen
            ? theme.palette.text.primary
            : theme.palette.common.white,
          fontWeight: 500,
          mb: 1.5,
          fontSize: "16px",
        }}
      >
        {label}
      </Typography>

      {document ? (
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          sx={{
            p: 1.5,
            border: `1px solid ${theme.palette.border.subtle}`,
            borderRadius: "12px",
            backgroundColor: theme.palette.common.white,
            opacity: isProcessing ? 0.7 : 1,
            pointerEvents: isProcessing ? "none" : "auto",
          }}
        >
          <Box display="flex" alignItems="center" gap={1} sx={{ flex: 1, minWidth: 0 }}>
            {/* The icon follows the real extension, so a .pdf can never show a
                DOC icon (UI/UX audit 2.3.4). */}
            <FileTypeIcon
              kind={docKindFor(
                "name" in document ? document.name : document.tittle,
              )}
              size={40}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body1"
                noWrap
                color={theme.palette.text.tertiary}
              >
                {displayFileName(
                  "tittle" in document ? document.tittle : document.name,
                )}
              </Typography>
              <Typography
                variant="caption"
                noWrap
                color={theme.palette.text.secondary}
              >
                {/* `documentSize` is a raw byte count from the API — format it. */}
                {"size" in document
                  ? document.size
                  : formatSizeInMB(document.documentSize)}{" "}
                •{" "}
                {"createdDate" in document && document.createdDate
                  ? formatDate(document.createdDate)
                  : formatDate(new Date())}
              </Typography>
            </Box>
          </Box>
          <Tooltip title={t("functionalities.files.deselect")}>
            <span>
              <Fab
                size="small"
                color="default"
                variant="circular"
                appearance="soft"
                onClick={onRemove}
                disabled={isProcessing}
                aria-label={t("functionalities.files.deselect")}
                title={t("functionalities.files.deselect")}
                sx={{
                  flexShrink: 0,
                  backgroundColor: theme.palette.border.soft,
                  color: theme.palette.primary.dark,
                }}
              >
                <UiIcon name="close" />
              </Fab>
            </span>
          </Tooltip>
        </Box>
      ) : canUpload ? (
        <Box
          border={`2px dashed ${
            isDragOver
              ? alpha(theme.palette.primary.main, 0.7)
              : alpha(theme.palette.grey[500], 0.3)
          }`}
          borderRadius={2}
          p={3}
          display="flex"
          alignItems="center"
          justifyContent="center"
          bgcolor={
            isDragOver
              ? alpha(theme.palette.primary.main, 0.08)
              : alpha(theme.palette.grey[500], 0.08)
          }
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            cursor: isProcessing ? "not-allowed" : "pointer",
            minHeight: 100,
            transition: "all 0.3s ease",
            opacity: isProcessing ? 0.5 : 1,
            pointerEvents: isProcessing ? "none" : "auto",
            "&:hover": !isProcessing
              ? {
                  borderColor: alpha(theme.palette.primary.main, 0.5),
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                }
              : {},
          }}
        >
          <Box
            textAlign="center"
            width="100%"
            onClick={
              !isProcessing
                ? () =>
                    window.document
                      .getElementById(inputId)
                      ?.click()
                : undefined
            }
          >
            <input
              type="file"
              id={inputId}
              hidden
              accept=".pdf,.docx"
              onChange={handleFileChange}
              disabled={isProcessing}
            />
            <Icon
              icon="eva:cloud-upload-fill"
              fontSize={40}
              color={
                isDragOver
                  ? theme.palette.primary.main
                  : dropzoneTextColor
              }
              style={{ marginBottom: "8px" }}
            />
            <Typography
              variant="body2"
              color={
                isDragOver ? theme.palette.primary.main : dropzoneTextColor
              }
              sx={{ fontWeight: 500 }}
            >
              {isDragOver
                ? t("functionalities.page.dropFileHere")
                : t("functionalities.page.uploadFile")}
            </Typography>
            <Typography
              variant="caption"
              color={dropzoneTextColor}
              sx={{ mt: 0.5, display: "block" }}
            >
              {isDragOver
                ? t("functionalities.files.releaseToUpload")
                : t("functionalities.page.clickOrDragFile")}
              <br />
              {t("functionalities.page.supportedFileHint")}
            </Typography>
          </Box>
        </Box>
      ) : (
        <Box
          border={`2px dashed ${alpha(theme.palette.error.main, 0.3)}`}
          borderRadius={2}
          p={3}
          display="flex"
          alignItems="center"
          justifyContent="center"
          bgcolor={alpha(theme.palette.error.main, 0.05)}
          sx={{
            minHeight: 100,
          }}
        >
          <Box textAlign="center">
            <Icon
              icon="mingcute:lock-line"
              fontSize={40}
              color={noAccessColor}
              style={{ marginBottom: "8px" }}
            />
            <Typography
              variant="body2"
              color={noAccessColor}
              sx={{ fontWeight: 500 }}
            >
              {t("functionalities.page.noUploadRights")}
            </Typography>
            <Typography
              variant="caption"
              color={noAccessColor}
              sx={{ mt: 0.5, display: "block" }}
            >
              {t("functionalities.page.contactAdminForAccess")}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};
  
  /** Neutral row action: download. */
  const renderDownloadAction = (doc: UserDocumentResponse) => (
    <Tooltip title={t("functionalities.files.downloadDocument")}>
      <span>
        <Fab
          size="small"
          color="default"
          variant="circular"
          appearance="soft"
          aria-label={t("functionalities.files.downloadDocument")}
          onClick={() => handleDownloadDocument(doc)}
          disabled={isProcessing || isLoading}
          sx={{
            backgroundColor: theme.palette.border.soft,
            color: theme.palette.primary.dark,
          }}
        >
          <UiIcon name="download" />
        </Fab>
      </span>
    </Tooltip>
  );

  /**
   * Destructive row action: permanent delete. Still routes through
   * ConfirmationDialog (UI/UX audit 2.3.3).
   */
  const renderDeleteAction = (doc: UserDocumentResponse) => (
    <Tooltip title={t("functionalities.page.deleteDocument")}>
      <span>
        <Fab
          size="small"
          color="default"
          variant="circular"
          appearance="soft"
          aria-label={t("functionalities.page.deleteDocument")}
          onClick={() => handleDocumentRemove(doc)}
          disabled={isProcessing || isLoading}
          sx={{
            backgroundColor: theme.palette.border.soft,
            color: theme.palette.primary.dark,
          }}
        >
          <UiIcon name="trash" />
        </Fab>
      </span>
    </Tooltip>
  );

  const noSelectionRights = (
    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
      <Typography
        variant="caption"
        color={theme.palette.text.secondary}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          fontStyle: "italic",
          py: 1,
        }}
      >
        <Icon icon="mingcute:lock-line" style={{ fontSize: 14 }} />
        {t("aiInsight.page.noSelectPermission")}
      </Typography>
    </Box>
  );

  /**
   * Row actions for the documents list. `inDrawer` only changes what happens
   * after a multi-select (the bottom drawer closes) and whether a module must
   * already be picked — both behaviours are carried over unchanged.
   */
  const renderDocumentActions = (
    doc: UserDocumentResponse,
    inDrawer: boolean
  ) => {
    // Request Specs & Document Contradiction - two document selection
    if (
      selectedModule === "Request Specs" ||
      selectedModule === "Document Contradiction"
    ) {
      const isRequestSpecs = selectedModule === "Request Specs";
      const canCreateForModule = isRequestSpecs
        ? canCreateRequestSpecs
        : canCreateDocumentContradiction;
      const buttonLabels = [
        t("requestSpecs.bestek1"),
        t("requestSpecs.bestek2"),
      ];
      const doc1 = isRequestSpecs ? document1 : contradictionReferenceDocument;
      const doc2 = isRequestSpecs ? document2 : contradictionTargetDocument;

      if (!canCreateForModule) return noSelectionRights;

      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 0.5,
            alignItems: "center",
          }}
        >
          {renderDownloadAction(doc)}
          {renderDeleteAction(doc)}
          {buttonLabels.map((label, index) => {
            const isSelected =
              (doc1 && "id" in doc1 && doc1.id === doc.id && index === 0) ||
              (doc2 && "id" in doc2 && doc2.id === doc.id && index === 1);
            const disabledReason = isProcessing
              ? t("functionalities.page.processingWait")
              : null;

            return (
              // A disabled MUI Button fires no pointer events — the span keeps
              // the tooltip reachable on hover (UI/UX audit 2.3.1).
              <Tooltip key={label} title={disabledReason ?? ""}>
                <span>
                  <Button
                    variant={isSelected ? "contained" : "text"}
                    color={isSelected ? "primary" : "inherit"}
                    size="medium"
                    onClick={() => {
                      if (isRequestSpecs) {
                        handleDocumentSelect(doc, (index + 1) as 1 | 2);
                      } else {
                        handleDocumentSelect(doc, (index + 1) as 1 | 2, true);
                      }
                    }}
                    disabled={!!disabledReason}
                    sx={{
                      textTransform: "none",
                      fontWeight: 500,
                      ...(isSelected
                        ? {
                            border: `1px solid ${theme.palette.brand.navy}`,
                            backgroundColor: theme.palette.status.runningBg,
                            // Ink on cyan is navy, never white (audit 2.1.1).
                            color: theme.palette.primary.contrastText,
                            "&:hover": {
                              backgroundColor: theme.palette.status.runningBg,
                            },
                          }
                        : {
                            bgcolor: theme.palette.border.soft,
                            color: theme.palette.text.primary,
                          }),
                    }}
                    startIcon={
                      isSelected ? (
                        <Icon
                          icon="mingcute:check-circle-fill"
                          style={{ fontSize: 20 }}
                        />
                      ) : (
                        <Icon
                          icon="mingcute:check-circle-line"
                          style={{ fontSize: 20 }}
                        />
                      )
                    }
                  >
                    {isSelected ? t("aiInsight.page.selectedLabel") : label}
                  </Button>
                </span>
              </Tooltip>
            );
          })}
        </Box>
      );
    }

    // AI Insight and Company Standard Document - multiple document selection
    const canCreateForModule =
      selectedModule === "AI Insight"
        ? canCreateAIInsight
        : canCreateCompanyStandard;
    const isSelected = multipleSelectedDocuments.some((d) => d.id === doc.id);
    const selectedCount =
      multipleFiles.length + multipleSelectedDocuments.length;
    const canSelect = selectedCount < 5;

    if (!canCreateForModule) return noSelectionRights;

    const disabledReason = !inDrawer && !selectedModule
      ? t("functionalities.page.chooseFunctionalityFirst")
      : isProcessing || isLoading
        ? t("functionalities.page.processingWait")
        : !isSelected && !canSelect
          ? t("functionalities.page.maxDocsReached")
          : null;

    return (
      <Box
        gap={0.5}
        display={"flex"}
        flexDirection={"row"}
        alignItems={"center"}
        justifyContent={"flex-end"}
        sx={{ flexShrink: 0, flexWrap: "nowrap" }}
      >
        {renderDownloadAction(doc)}
        {renderDeleteAction(doc)}
        <Tooltip title={disabledReason ?? ""}>
          <span>
            <Button
              key="multiple-select"
              variant={isSelected ? "contained" : "outlined"}
              size="small"
              onClick={() => {
                if (isSelected) {
                  handleMultipleDocumentDeselect(doc.id);
                } else {
                  handleMultipleDocumentSelect(doc);
                  if (inDrawer) handleCloseBottomDrawer();
                }
              }}
              disabled={!!disabledReason}
              sx={{
                minWidth: 100,
                maxWidth: 120,
                borderRadius: 20,
                textTransform: "none",
                fontWeight: 500,
                fontSize: "12px",
                px: 2,
                py: 0.5,
                flexShrink: 0,
                whiteSpace: "nowrap",
                ...(isSelected
                  ? {
                      bgcolor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                      border: `1px solid ${theme.palette.primary.main}`,
                      "&:hover": {
                        bgcolor: theme.palette.primary.dark,
                        border: `1px solid ${theme.palette.primary.dark}`,
                      },
                    }
                  : {
                      bgcolor: "transparent",
                      color: theme.palette.text.primary,
                      border: `1px solid ${alpha(
                        theme.palette.primary.main,
                        0.5
                      )}`,
                      "&:hover": {
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        border: `1px solid ${theme.palette.primary.main}`,
                      },
                    }),
              }}
              startIcon={
                isSelected ? (
                  <Icon
                    icon="mingcute:check-circle-fill"
                    style={{ fontSize: 16 }}
                  />
                ) : (
                  <Icon
                    icon="mingcute:add-circle-line"
                    style={{ fontSize: 16 }}
                  />
                )
              }
            >
              {isSelected
                ? t("aiInsight.page.selectedLabel")
                : t("aiInsight.page.selectButtonLabel", { count: selectedCount })}
            </Button>
          </span>
        </Tooltip>
      </Box>
    );
  };

  const MiddleContent = (
    <>
      {/* Module Selection Dropdown */}
      <Box display="flex" flexDirection="column" gap={2}>
      <StyledSelect
        label={t("functionalities.page.moduleLabel")}
        value={selectedModule}
        onChange={(event) => setSelectedModule(event.target.value as string)}
        options={[
          canCreateRequestSpecs && { label: t("functionalities.requestSpecs"), value: "Request Specs" },
          canCreateAIInsight && { label: t("functionalities.aiInsight"), value: "AI Insight" },
          canCreateCompanyStandard && { label: t("functionalities.companyStandard"), value: "Company Standard Document" },
        ].filter((option): option is { label: string; value: string } => Boolean(option))}
        disabled={isProcessing || isLoading}
      />

      {error && (
        <Alert ref={errorRef} severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Processing Error */}
      {processingError && (
        <Card
          sx={{
            backgroundColor: alpha(theme.palette.error.main, 0.1),
            border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
            mb: 2,
            flexShrink: 0,
            alignSelf: "stretch",
          }}
        >
          <CardContent sx={{ p: 2 }}>
            <Box display="flex" alignItems="flex-start" gap={2}>
              <Icon
                icon="mingcute:alert-triangle-fill"
                style={{ fontSize: 24, color: theme.palette.error.main }}
              />
              <Box flex={1}>
                <Typography
                  variant="body2"
                  fontWeight={500}
                  color={theme.palette.error.dark}
                  mb={1}
                >
                  {selectedModule === "Request Specs"
                    ? t("functionalities.page.comparisonFailed")
                    : selectedModule === "Document Contradiction"
                    ? t("functionalities.page.contradictionFailed")
                    : selectedModule === "AI Insight"
                    ? t("functionalities.page.analysisFailed")
                    : t("upload.uploadFailed")}{" "}
                  {t("functionalities.page.afterLabel")} {formatTime(processingTime)}
                  {retryCount > 0 &&
                    ` ${t("aiInsight.multiUpload.attemptsSuffix", {
                      count: retryCount + 1,
                    })}`}
                </Typography>
                <Typography
                  variant="caption"
                  color={theme.palette.error.dark}
                  mb={2}
                >
                  {processingError}
                </Typography>
                <Box display="flex" gap={1}>
                  <Button
                    size="small"
                    variant="contained"
                    color="error"
                    onClick={handleRetryProcessing}
                    startIcon={<Icon icon="mingcute:refresh-2-line" />}
                  >
                    {t("common.retry")}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setProcessingError(null);
                      setIsProcessing(false);
                    }}
                    sx={{
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {t("functionalities.page.reset")}
                  </Button>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Processing Status */}
      {isProcessing && (
        <Card
          sx={{
            backgroundColor: alpha(theme.palette.grey[900], 0.8),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
            mb: 2,
            backdropFilter: "blur(10px)",
            borderRadius: 2,
            flexShrink: 0,
            alignSelf: "stretch",
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box display="flex" alignItems="flex-start" gap={2}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  backgroundColor: alpha(theme.palette.primary.main, 0.2),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CircularProgress size={20} thickness={4} color="primary" />
              </Box>
              <Box flex={1}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={1}
                >
                  <Typography
                    variant="body1"
                    fontWeight={600}
                    color={theme.palette.primary.light}
                    sx={{ fontSize: "1.1rem" }}
                  >
                    {selectedModule === "Request Specs"
                      ? t("functionalities.page.comparisonInProgress")
                      : selectedModule === "Document Contradiction"
                      ? t("functionalities.page.contradictionInProgress")
                      : t("aiInsight.multiUpload.analysisInProgress")}
                  </Typography>
                  
                </Box>
                <Typography
                  variant="body2"
                  color={alpha(theme.palette.primary.light, 0.8)}
                  mb={2}
                  sx={{ fontSize: "0.875rem" }}
                >
                  {t("aiInsight.multiUpload.timeLabel", {
                    time: formatTime(processingTime),
                  })}
                  {retryCount > 0 && (
                    <Chip
                      label={t("aiInsight.multiUpload.attemptChip", {
                        count: retryCount + 1,
                      })}
                      size="small"
                      color="secondary"
                      sx={{
                        ml: 1,
                        backgroundColor: alpha(
                          theme.palette.secondary.main,
                          0.2
                        ),
                        color: theme.palette.secondary.light,
                        fontSize: "0.75rem",
                      }}
                    />
                  )}
                </Typography>

                {/* Progress steps */}
                <Box display="flex" flexDirection="column" gap={1.5} mt={2}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      backgroundColor: alpha(theme.palette.success.main, 0.1),
                      border: `1px solid ${alpha(
                        theme.palette.success.main,
                        0.2
                      )}`,
                    }}
                  >
                    <Typography
                      variant="body2"
                      color={theme.palette.success.light}
                      sx={{ fontWeight: 500 }}
                    >
                      {t("aiInsight.multiUpload.uploadToAI")}
                    </Typography>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        backgroundColor: theme.palette.success.main,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Icon
                        icon="mingcute:check-line"
                        style={{
                          fontSize: 14,
                          color: theme.palette.success.contrastText,
                        }}
                      />
                    </Box>
                  </Box>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      border: `1px solid ${alpha(
                        theme.palette.primary.main,
                        0.3
                      )}`,
                    }}
                  >
                    <Typography
                      variant="body2"
                      color={theme.palette.primary.light}
                      sx={{ fontWeight: 500 }}
                    >
                      {selectedModule === "Request Specs"
                        ? t("functionalities.page.runComparison")
                        : t("aiInsight.multiUpload.runningAnalysis")}
                    </Typography>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        backgroundColor: alpha(theme.palette.primary.main, 0.2),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CircularProgress
                        size={14}
                        thickness={5}
                        color="primary"
                      />
                    </Box>
                  </Box>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      backgroundColor: alpha(theme.palette.grey[700], 0.3),
                      border: `1px solid ${alpha(
                        theme.palette.grey[600],
                        0.3
                      )}`,
                      opacity: 0.6,
                    }}
                  >
                    <Typography
                      variant="body2"
                      // Dark processing card: `text.disabled` is unreadable here.
                      color={alpha(theme.palette.common.white, 0.7)}
                      sx={{ fontWeight: 500 }}
                    >
                      {t("aiInsight.multiUpload.generatingResults")}
                    </Typography>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        border: `2px solid ${theme.palette.divider}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    />
                  </Box>
                </Box>

                {/* Time-based status messages */}
                {processingTime >= 600 && (
                  <Box
                    mt={2}
                    p={2}
                    bgcolor={alpha(theme.palette.warning.main, 0.15)}
                    border={`1px solid ${alpha(
                      theme.palette.warning.main,
                      0.3
                    )}`}
                    borderRadius={1.5}
                    sx={{ backdropFilter: "blur(5px)" }}
                  >
                    <Typography
                      variant="body2"
                      color={theme.palette.warning.light}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        fontWeight: 500,
                      }}
                    >
                      <Icon
                        icon="mingcute:alert-triangle-line"
                        style={{ fontSize: 16, marginRight: 8 }}
                      />
                      {t("requestSpecs.longProcessingTime")}
                    </Typography>
                  </Box>
                )}
                {processingTime >= 300 && processingTime < 600 && (
                  <Box
                    mt={2}
                    p={2}
                    bgcolor={alpha(theme.palette.info.main, 0.15)}
                    border={`1px solid ${alpha(theme.palette.info.main, 0.3)}`}
                    borderRadius={1.5}
                    sx={{ backdropFilter: "blur(5px)" }}
                  >
                    <Typography
                      variant="body2"
                      color={theme.palette.info.light}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        fontWeight: 500,
                      }}
                    >
                      <Icon
                        icon="mingcute:time-line"
                        style={{ fontSize: 16, marginRight: 8 }}
                      />
                      {t("requestSpecs.longerThanExpected")}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Request Specs Form */}
      {selectedModule === "Request Specs" && (
        <RequestSpecsForm
          comparisonName={comparisonName}
          onComparisonNameChange={setComparisonName}
          comparisonType={comparisonType}
          onComparisonTypeChange={setComparisonType}
          document1={document1}
          document2={document2}
          renderDocumentSlot={(doc, pos, label) => 
            renderDocumentSlot(
              doc,
              label,
              `file-upload-input-${pos}`,
              canCreateRequestSpecs,
              () => handleRemoveFile(pos, false)
            )
          }
          isProcessing={isProcessing}
          isSmallScreen={isSmallScreen}
          onOpenBottomDrawer={handleOpenBottomDrawer}
        />
      )}

      {/* Document Contradiction Form */}
      {selectedModule === "Document Contradiction" && (
        <DocumentContradictionForm
          contradictionName={contradictionName}
          onContradictionNameChange={setContradictionName}
          document1={contradictionReferenceDocument}
          document2={contradictionTargetDocument}
          renderDocumentSlot={(doc, pos, label) => 
            renderDocumentSlot(
              doc,
              label,
              `file-upload-input-contradiction-${pos}`,
              canCreateDocumentContradiction,
              () => handleRemoveFile(pos, true)
            )
          }
          isProcessing={isProcessing}
          isSmallScreen={isSmallScreen}
          onOpenBottomDrawer={handleOpenBottomDrawer}
        />
      )}

      {/* AI Insight Form */}
      {selectedModule === "AI Insight" && (
        <AIInsightForm
          analysisType={analysisType}
          onAnalysisTypeChange={setAnalysisType}
          customInstructions={customInstructions}
          onCustomInstructionsChange={setCustomInstructions}
          multipleFiles={multipleFiles}
          multipleSelectedDocuments={multipleSelectedDocuments}
          onFileRemove={handleMultipleFileRemove}
          onDocumentDeselect={handleMultipleDocumentDeselect}
          onFileUpload={handleMultipleFileUpload}
          onClearAll={handleClearAllMultipleSelections}
          onDownloadDocument={handleDownloadDocument}
          isProcessing={isProcessing}
          isSmallScreen={isSmallScreen}
          onOpenBottomDrawer={handleOpenBottomDrawer}
        />
      )}

      {/* Company Standard Document Form */}
      {selectedModule === "Company Standard Document" && (
        <CompanyStandardForm
          customInstructions={customInstructions}
          onCustomInstructionsChange={setCustomInstructions}
          companyRules={companyRules}
          selectedRuleIds={selectedRuleIds}
          onSelectedRuleIdsChange={setSelectedRuleIds}
          rulesLoading={rulesLoading}
          noRulesError={noRulesError}
          multipleFiles={multipleFiles}
          multipleSelectedDocuments={multipleSelectedDocuments}
          onFileRemove={handleMultipleFileRemove}
          onDocumentDeselect={handleMultipleDocumentDeselect}
          onFileUpload={handleMultipleFileUpload}
          onClearAll={handleClearAllMultipleSelections}
          onDownloadDocument={handleDownloadDocument}
          isProcessing={isProcessing}
          selectedCompanyId={selectedCompanyId || ""}
          isSmallScreen={isSmallScreen}
          onOpenBottomDrawer={handleOpenBottomDrawer}
        />
      )}
      </Box>
    </>
  );

  const canStartComparison =
    selectedModule === "Request Specs"
      ? (comparisonType === "Content Comparison" ? canCreateDocumentContradiction : canCreateRequestSpecs) &&
        document1 &&
        document2 &&
        comparisonName &&
        !isProcessing
      : selectedModule === "Document Contradiction"
      ? canCreateDocumentContradiction &&
        contradictionReferenceDocument &&
        contradictionTargetDocument &&
        contradictionName &&
        !isProcessing
      : selectedModule === "AI Insight"
      ? canCreateAIInsight &&
        (multipleFiles.length > 0 || multipleSelectedDocuments.length > 0) &&
        analysisType &&
        !isProcessing &&
        !isLoading &&
        (analysisType !== "Question" || customInstructions.trim())
      : selectedModule === "Company Standard Document"
      ? canCreateCompanyStandard &&
        (multipleFiles.length > 0 || multipleSelectedDocuments.length > 0) &&
        !isProcessing &&
        !isLoading &&
        companyRules.length > 0
      : false;

  const getButtonText = () => {
    if (selectedModule === "Request Specs") {
      if (!canCreateRequestSpecs) return t("requestSpecs.noPermissions");
      if (isProcessing) return t("functionalities.page.processingButton");
      if (!comparisonName) return t("functionalities.page.enterName");
      if (!document1 || !document2)
        return t("functionalities.page.selectTwoDocuments");
      return t("functionalities.page.compareNow");
    } else if (selectedModule === "Document Contradiction") {
      if (!canCreateDocumentContradiction)
        return t("aiInsight.page.noAnalysisPermission");
      if (isProcessing) return t("aiInsight.page.analysisInProgressButton");
      if (!contradictionName) return t("functionalities.page.enterName");
      if (!contradictionReferenceDocument || !contradictionTargetDocument)
        return t("functionalities.page.selectTwoDocuments");
      return t("functionalities.page.analyzeContradictions");
    } else if (selectedModule === "AI Insight") {
      if (!canCreateAIInsight) return t("aiInsight.page.noAnalysisPermission");
      if (isProcessing) return t("aiInsight.page.analysisInProgressButton");
      if (isLoading) return t("aiInsight.page.uploadingButton");
      if (processingError) return t("common.retry");
      const totalDocs = multipleFiles.length + multipleSelectedDocuments.length;
      if (totalDocs === 0) return t("aiInsight.page.selectDocumentsButton");
      if (!analysisType) return t("functionalities.page.selectAnalysisTypeShort");
      if (analysisType === "Question" && !customInstructions.trim())
        return t("functionalities.page.enterQuestion");
      return t("aiInsight.page.startAnalysisButton", { count: totalDocs });
    } else if (selectedModule === "Company Standard Document") {
      if (!canCreateCompanyStandard)
        return t("functionalities.page.noUploadRightsButton");
      if (isProcessing) return t("functionalities.page.uploadInProgress");
      if (isLoading) return t("aiInsight.page.uploadingButton");
      if (processingError) return t("common.retry");
      const totalDocs = multipleFiles.length + multipleSelectedDocuments.length;
      if (totalDocs === 0) return t("aiInsight.page.selectDocumentsButton");
      if (companyRules.length === 0)
        return t("functionalities.page.configureRules");
      return t("functionalities.page.startUpload", { count: totalDocs });
    }
    return t("functionalities.page.start");
  };

  // AI Insight button handler
  const handleAIInsightButtonClick = async () => {
    if (!canCreateAIInsight) {
      setError(t("aiInsight.multiUpload.noCreatePermission"));
      return;
    }

    const totalDocuments =
      multipleFiles.length + multipleSelectedDocuments.length;
    if (totalDocuments === 0) {
      setError(t("aiInsight.multiUpload.selectAtLeastOne"));
      return;
    }
    if (totalDocuments > 5) {
      setError(t("aiInsight.multiUpload.maxFiveDocuments"));
      return;
    }

    if (!analysisType) {
      setError(t("aiInsight.multiUpload.selectAnalysisType"));
      return;
    }

    if (analysisType === "Question" && !customInstructions.trim()) {
      setError(t("aiInsight.multiUpload.questionRequired"));
      return;
    }

    if (isSuperAdmin && (!contextCompanyId || contextCompanyId === "all")) {
      setError(t("functionalities.page.selectCompanyFromDropdown"));
      return;
    }

    if (!isAuthenticated || !currentAccount) {
      setError(t("aiInsight.multiUpload.mustBeLoggedIn"));
      return;
    }

    try {
      setIsLoading(true);
      setIsProcessing(true);
      setError(null);
      setProcessingError(null);

      const userId = currentAccount.username;
      const isMultipleDocuments = totalDocuments > 1;

      const allDocumentNames = [
        ...multipleFiles.map((file) => file.name),
        ...multipleSelectedDocuments.map((doc) => doc.tittle),
      ];

      const getWorkflowType = (analysisType: string) => {
        switch (analysisType) {
          case "GegevensExtractie":
            return "dataanalysis";
          case "Question":
            return "question";
          case "DocumentSamenvatting":
          default:
            return "summarize";
        }
      };

      const workflowType = getWorkflowType(analysisType);

      if (multipleFiles.length > 0 && multipleSelectedDocuments.length === 0) {
        const files: FileUploadRequestDto[] = [];
        for (const file of multipleFiles) {
          const base64File = await fileToBase64(file);
          files.push({
            base64File,
            fileName: file.name,
            contentType: getContentType(file.name),
          });
        }

        const createDocumentRequest: CreateUserDocumentDto = {
          userId,
          tittle: `${analysisType} - ${allDocumentNames.join(" + ")}`,
          description: customInstructions || undefined,
          question: customInstructions || undefined,
          files,
          workflowType,
          typeOfDocument: analysisType,
          companyId: selectedCompanyId || undefined,
          companyName: undefined,
        };

        const result = await userDocumentApi.create(createDocumentRequest);

        const analysisData = {
          documentId: "generated-multiple-analysis",
          fileName: allDocumentNames.join(" + "),
          analysisType,
          customInstructions,
          isExistingDocument: false,
          isDualDocument: isMultipleDocuments,
          analysisResults: result,
          timestamp: new Date().toISOString(),
        };
        sessionStorage.setItem(
          ANALYSIS_RESULT_KEY,
          JSON.stringify(analysisData)
        );

        navigate("/document-analysis", {
          state: analysisData,
        });
      } else if (
        multipleSelectedDocuments.length > 0 &&
        multipleFiles.length === 0
      ) {
        const workflowFiles: FileUploadUrlDto[] = [];
        for (const document of multipleSelectedDocuments) {
          workflowFiles.push({
            fileUrl: document.documentLink,
            fileName: document.tittle,
            fileSize: document.documentSize || "0.0 MB",
            mimeType: getContentType(document.tittle || "document"),
          });
        }

        const updateDocumentRequest: UpdateUserDocumentDto = {
          userId,
          tittle: `${analysisType} - ${allDocumentNames.join(" + ")}`,
          description: customInstructions || undefined,
          question: customInstructions || undefined,
          workflowType,
          files: workflowFiles,
          typeOfDocument: analysisType,
          companyId: selectedCompanyId || undefined,
          companyName: undefined,
        };

        const result = await userDocumentApi.update(updateDocumentRequest);

        const analysisData = {
          documentId: "generated-multiple-analysis",
          fileName: allDocumentNames.join(" + "),
          analysisType,
          customInstructions,
          isExistingDocument: true,
          isDualDocument: isMultipleDocuments,
          analysisResults: result,
          timestamp: new Date().toISOString(),
        };
        sessionStorage.setItem(
          ANALYSIS_RESULT_KEY,
          JSON.stringify(analysisData)
        );

        navigate("/document-analysis", {
          state: analysisData,
        });
      } else {
        const mixedFiles: FileUploadRequestDto[] = [];
        const existingFiles: WorkflowDocumentDto[] = [];

        for (const file of multipleFiles) {
          const base64File = await fileToBase64(file);
          mixedFiles.push({
            base64File,
            fileName: file.name,
            contentType: getContentType(file.name),
          });
        }
        for (const document of multipleSelectedDocuments) {
            existingFiles.push({
            url: document.documentLink,
            name: document.tittle,
            size: document.documentSize || "0.0 MB",
            mimeType: getContentType(document.tittle || "document"),
          });
        }
        

        const createDocumentRequest: CreateUserDocumentDto = {
          userId,
          tittle: `${analysisType} - ${allDocumentNames.join(" + ")}`,
          description: customInstructions || undefined,
          question: customInstructions || undefined,
          files: mixedFiles,
          workflowType,
          typeOfDocument: analysisType,
          companyId: selectedCompanyId || undefined,
          companyName: undefined,
        };

        const result = await userDocumentApi.create(createDocumentRequest);

        const analysisData = {
          documentId: "generated-mixed-multiple-analysis",
          fileName: allDocumentNames.join(" + "),
          analysisType,
          customInstructions,
          isExistingDocument: false,
          isDualDocument: isMultipleDocuments,
          analysisResults: result,
          timestamp: new Date().toISOString(),
        };
        sessionStorage.setItem(
          ANALYSIS_RESULT_KEY,
          JSON.stringify(analysisData)
        );

        navigate("/document-analysis", {
          state: analysisData,
        });
      }
    } catch (err) {
      console.error("Error processing document:", err);
      setProcessingError(
        err instanceof Error
          ? err.message
          : "Er is een fout opgetreden tijdens het verwerken"
      );
    } finally {
      setIsLoading(false);
      setIsProcessing(false);
    }
  };

  // Company Standard Document button handler
  const handleCompanyStandardButtonClick = async () => {
    if (!canCreateCompanyStandard) {
      setError(
        t("aiInsight.multiUpload.noCreatePermission")
      );
      return;
    }

    const totalDocuments =
      multipleFiles.length + multipleSelectedDocuments.length;
    if (totalDocuments === 0) {
      setError(t("aiInsight.multiUpload.selectAtLeastOne"));
      return;
    }
    if (totalDocuments > 5) {
      setError(t("aiInsight.multiUpload.maxFiveDocuments"));
      return;
    }

    if (isSuperAdmin && (!contextCompanyId || contextCompanyId === "all")) {
      setError(t("functionalities.page.selectCompanyFromDropdown"));
      return;
    }

    if (selectedCompanyId && companyRules.length === 0 && !rulesLoading) {
      setError(t("functionalities.page.configureRulesFirst"));
      return;
    }

    if (!isAuthenticated || !currentAccount) {
      setError(t("aiInsight.multiUpload.mustBeLoggedIn"));
      return;
    }

    try {
      setIsLoading(true);
      setIsProcessing(true);
      setError(null);
      setProcessingError(null);

      const userId = currentAccount.username;
      const isMultipleDocuments = totalDocuments > 1;

      const allDocumentNames = [
        ...multipleFiles.map((file) => file.name),
        ...multipleSelectedDocuments.map((doc) => doc.tittle),
      ];

      const workflowType = "companystandard";

      if (multipleFiles.length > 0 && multipleSelectedDocuments.length === 0) {
        const files: FileUploadRequestDto[] = [];
        for (const file of multipleFiles) {
          const base64File = await fileToBase64(file);
          files.push({
            base64File,
            fileName: file.name,
            contentType: getContentType(file.name),
          });
        }

        const createDocumentRequest: CreateUserDocumentDto = {
          userId,
          tittle: `Bedrijfsstandaard - ${allDocumentNames.join(" + ")}`,
          description: customInstructions || undefined,
          question: customInstructions || undefined,
          files,
          workflowType,
          typeOfDocument: "CompanyStandard",
          companyId: selectedCompanyId || undefined,
          companyName: undefined,
          selectedRules:
            selectedRuleIds.length > 0 ? selectedRuleIds : undefined,
        };

        const result = await userDocumentApi.create(createDocumentRequest);

        const analysisData = {
          documentId: "generated-company-standard",
          fileName: allDocumentNames.join(" + "),
          analysisType: "CompanyStandard",
          customInstructions,
          isExistingDocument: false,
          isDualDocument: isMultipleDocuments,
          analysisResults: result,
          timestamp: new Date().toISOString(),
        };
        sessionStorage.setItem(
          COMPANY_STANDARD_DOCUMENTS_KEY,
          JSON.stringify(analysisData)
        );

        navigate("/company-standard-results", {
          state: analysisData,
        });
      } else if (
        multipleSelectedDocuments.length > 0 &&
        multipleFiles.length === 0
      ) {
        const workflowFiles: FileUploadUrlDto[] = [];
        for (const document of multipleSelectedDocuments) {
          workflowFiles.push({
            fileUrl: document.documentLink,
            fileName: document.tittle,
            fileSize: document.documentSize || "0.0 MB",
            mimeType: getContentType(document.tittle || "document"),
          });
        }

        const updateDocumentRequest: UpdateUserDocumentDto = {
          userId,
          tittle: `Bedrijfsstandaard - ${allDocumentNames.join(" + ")}`,
          description: customInstructions || undefined,
          question: customInstructions || undefined,
          workflowType,
          files: workflowFiles,
          typeOfDocument: "CompanyStandard",
          companyId: selectedCompanyId || undefined,
          companyName: undefined,
          selectedRules:
            selectedRuleIds.length > 0 ? selectedRuleIds : undefined,
        };

        const result = await userDocumentApi.update(updateDocumentRequest);

        const analysisData = {
          documentId: "generated-company-standard",
          fileName: allDocumentNames.join(" + "),
          analysisType: "CompanyStandard",
          customInstructions,
          isExistingDocument: true,
          isDualDocument: isMultipleDocuments,
          analysisResults: result,
          timestamp: new Date().toISOString(),
        };
        sessionStorage.setItem(
          COMPANY_STANDARD_DOCUMENTS_KEY,
          JSON.stringify(analysisData)
        );

        navigate("/company-standard-results", {
          state: analysisData,
        });
      } else {
        const mixedFiles: FileUploadRequestDto[] = [];
        const existingFiles: WorkflowDocumentDto[] = [];

        for (const file of multipleFiles) {
          const base64File = await fileToBase64(file);
          mixedFiles.push({
            base64File,
            fileName: file.name,
            contentType: getContentType(file.name),
          });
        }
        for (const document of multipleSelectedDocuments) {
            existingFiles.push({
            url: document.documentLink,
            name: document.tittle,
            size: document.documentSize || "0.0 MB",
            mimeType: getContentType(document.tittle || "document"),
          });
        }

        const createDocumentRequest: CreateUserDocumentDto = {
          userId,
          tittle: `Bedrijfsstandaard - ${allDocumentNames.join(" + ")}`,
          description: customInstructions || undefined,
          question: customInstructions || undefined,
          files: mixedFiles,
          workflowType,
          typeOfDocument: "CompanyStandard",
          companyId: selectedCompanyId || undefined,
          companyName: undefined,
          selectedRules:
            selectedRuleIds.length > 0 ? selectedRuleIds : undefined,
        };

        const result = await userDocumentApi.create(createDocumentRequest);

        const analysisData = {
          documentId: "generated-company-standard-mixed",
          fileName: allDocumentNames.join(" + "),
          analysisType: "CompanyStandard",
          customInstructions,
          isExistingDocument: false,
          isDualDocument: isMultipleDocuments,
          analysisResults: result,
          timestamp: new Date().toISOString(),
        };
        sessionStorage.setItem(
          COMPANY_STANDARD_DOCUMENTS_KEY,
          JSON.stringify(analysisData)
        );

        navigate("/company-standard-results", {
          state: analysisData,
        });
      }
    } catch (err) {
      console.error("Error processing document:", err);
      setProcessingError(
        err instanceof Error
          ? err.message
          : "Er is een fout opgetreden tijdens het verwerken"
      );
    } finally {
      setIsLoading(false);
      setIsProcessing(false);
    }
  };

  // Poll job status for Document Contradiction
  const pollJobStatus = useCallback(async (jobId: string) => {
    try {
      const statusResponse = await userDocumentApi.getJobStatus(jobId);
      
      if (statusResponse.status === "completed") {
        // Stop polling
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        setPollingJobId(null);
        setIsProcessing(false);
        
        // Extract document URLs from files array
        const referenceFile = statusResponse.files?.find((f) => f.fileType === 'pdf') || statusResponse.files?.[0];
        const targetFile = statusResponse.files?.[1];
        
        // Navigate to results with the raw response data (matching history format)
        const contradictionData = {
          documentId: jobId,
          fileName: contradictionName,
          contradictionName,
          analysisResults: statusResponse.response,
          referenceDocumentUrl: buildBlobProxyUrl(referenceFile?.fileUrl || ""),
          targetDocumentUrl: buildBlobProxyUrl(targetFile?.fileUrl || ""),
          timestamp: new Date().toISOString(),
        };
        
        sessionStorage.setItem(
          CONTRADICTION_RESULT_KEY,
          JSON.stringify(contradictionData)
        );
        
        navigate("/document-contradiction", {
          state: contradictionData,
        });
      } else if (statusResponse.status === "failed") {
        // Stop polling and show error
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        setPollingJobId(null);
        setIsProcessing(false);
        setError(null);
        setProcessingError(
          t("functionalities.page.contradictionFailed")
        );
      }
      // If status is still "processing", keep polling
    } catch (err) {
      // HTTP error occurred - stop polling and show error
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      setPollingJobId(null);
      setIsProcessing(false);
      setError(null);
      setProcessingError(
        err instanceof Error ? err.message : "Er is een fout opgetreden bij het ophalen van de status"
      );
    }
  }, [contradictionName, navigate, CONTRADICTION_RESULT_KEY]);

  // Document Contradiction button handler
  const handleDocumentContradictionButtonClick = async () => {
    if (!canCreateDocumentContradiction) {
      setError(t("functionalities.page.noContradictionPermission"));
      return;
    }

    if (!contradictionReferenceDocument || !contradictionTargetDocument || !contradictionName) {
      setError(t("functionalities.page.fillFieldsAnalysis"));
      return;
    }

    // Company validation for super admin users
    if (isSuperAdmin && (!contextCompanyId || contextCompanyId === "all")) {
      setError(t("functionalities.page.selectCompanyFromDropdown"));
      return;
    }

    if (!isAuthenticated || !currentAccount) {
      setError(t("functionalities.page.mustBeLoggedInContradiction"));
      return;
    }

    try {
      setIsProcessing(true);
      setError(null);
      setProcessingError(null);

      const userId = currentAccount.username;
      const companyId = selectedCompanyId || undefined;

      // Process reference document
      let referenceFile: FileUploadRequestDto | undefined;
      let referenceFileUrl: string | undefined;

      if ("file" in contradictionReferenceDocument && contradictionReferenceDocument.file) {
        const base64File = await fileToBase64(contradictionReferenceDocument.file);
        referenceFile = {
          base64File,
          fileName: contradictionReferenceDocument.file.name,
          contentType: getContentType(contradictionReferenceDocument.file.name),
        };
      } else {
        const existingDoc = contradictionReferenceDocument as UserDocumentResponse;
        referenceFileUrl = existingDoc.documentLink;
      }

      // Process target document
      let targetFile: FileUploadRequestDto | undefined;
      let targetFileUrl: string | undefined;

      if ("file" in contradictionTargetDocument && contradictionTargetDocument.file) {
        const base64File = await fileToBase64(contradictionTargetDocument.file);
        targetFile = {
          base64File,
          fileName: contradictionTargetDocument.file.name,
          contentType: getContentType(contradictionTargetDocument.file.name),
        };
      } else {
        const existingDoc = contradictionTargetDocument as UserDocumentResponse;
        targetFileUrl = existingDoc.documentLink;
      }

      // Prepare API request
      const createContradictionRequest: CreateDocumentContradictionDto = {
        userId,
        companyId,
        title: contradictionName,
        workflowType: "documentcontradiction",
        description: "Document contradiction analysis",
        referenceFile,
        referenceFileUrl,
        targetFile,
        targetFileUrl,
      };

      // Make API call
      const result: WorkflowResponseDto = await userDocumentApi.createDocumentContradiction(
        createContradictionRequest
      );

      // Check the status
      if (result.status === "processing" && result.job_id) {
        const jobId = result.job_id;
        
        // Clear any existing interval first
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        
        // Start polling
        setPollingJobId(jobId);
        
        // Use a temporary marker to track if we should continue
        const tempMarker = {};
        pollingIntervalRef.current = tempMarker as any;
        
        // Poll immediately first time
        await pollJobStatus(jobId);
        
        // Only set up interval if polling wasn't stopped by the first call
        // Check if the marker is still there (not cleared by pollJobStatus)
        if (pollingIntervalRef.current !== tempMarker) {
          // First poll stopped polling, not setting up interval
        } else {
          // Then poll every 10 seconds
          pollingIntervalRef.current = setInterval(() => {
            pollJobStatus(jobId);
          }, 10000);
        }
        
      } else if (result.status === "completed") {
        // Immediate completion (unlikely but handle it)
        setIsProcessing(false);
        
        const contradictionData = {
          documentId: result.job_id || "generated-contradiction",
          fileName: contradictionName,
          contradictionName,
          analysisResults: result.data,
          referenceDocumentUrl: buildBlobProxyUrl(referenceFileUrl || ""),
          targetDocumentUrl: buildBlobProxyUrl(targetFileUrl || ""),
          timestamp: new Date().toISOString(),
        };
        
        sessionStorage.setItem(
          CONTRADICTION_RESULT_KEY,
          JSON.stringify(contradictionData)
        );
        
        navigate("/document-contradiction", {
          state: contradictionData,
        });
      } else if (result.status === "failed") {
        setIsProcessing(false);
        setProcessingError(
          result.message || t("functionalities.page.contradictionFailed")
        );
      }
    } catch (err) {
      console.error("Error creating contradiction analysis:", err);
      setIsProcessing(false);
      setProcessingError(
        err instanceof Error ? err.message : "Er is een fout opgetreden tijdens het verwerken"
      );
    }
  };

  const handleButtonClick = () => {
    if (selectedModule === "Request Specs") {
      handleComparisonButtonClick();
    } else if (selectedModule === "Document Contradiction") {
      handleDocumentContradictionButtonClick();
    } else if (selectedModule === "AI Insight") {
      handleAIInsightButtonClick();
    } else if (selectedModule === "Company Standard Document") {
      handleCompanyStandardButtonClick();
    }
  };

  return (
    <Box sx={{ height: "100%", minHeight: 0, display: "flex", flexDirection: "column" }}>
      <HeaderWithButton
        eyebrow="Bestek AI"
        title={t("functionalities.page.title")}
        description={t("functionalities.page.description")}
        showCompanyDropdown={true}
      />

      {/* Error handling */}
      {processingError && !isProcessing && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {processingError}
          <Button size="small" onClick={handleRetryProcessing} sx={{ ml: 2 }}>
            {t("common.retry")}
          </Button>
        </Alert>
      )}

      <Grid container spacing={2} sx={{ flex: 1, minHeight: 0 }}>
        {isSmallScreen ? (
          <BDrawer
            open={bottomDrawerOpen}
            title={t("aiInsight.page.availableDocumentsTitle")}
            showCloseButton={true}
            onClose={handleCloseBottomDrawer}
            dialogActions={
              <>
                <Button
                  startIcon={
                    isButtonsLoading ? (
                      <CircularProgress size={16} />
                    ) : (
                      <RefreshIcon />
                    )
                  }
                  variant="outlined"
                  color="inherit"
                  size="large"
                  onClick={() => setRefreshTrigger((prev) => prev + 1)}
                  disabled={isButtonsLoading || !canRefreshDocuments}
                >
                  {t("aiInsight.page.refreshButton")}
                </Button>
              </>
            }
          >
            <DocumentsView
              screenName={SCREEN_NAME}
              refreshTrigger={refreshTrigger}
              renderActionButtons={(doc: UserDocumentResponse) =>
                renderDocumentActions(doc, true)
              }
            />
          </BDrawer>
        ) : (
          <Grid size={{ xs: 12, lg: 8 }} sx={{ height: "100%" }}>
            <Box
              sx={{
                height: "100%",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <DocumentsView
                screenName={SCREEN_NAME}
                refreshTrigger={refreshTrigger}
                renderActionButtons={(doc: UserDocumentResponse) =>
                  renderDocumentActions(doc, false)
                }
              />
            </Box>
          </Grid>
        )}

        <Grid size={{ xs: 12, lg: 4 }} sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <DocumentsUpload
            heading={
              selectedModule === "Request Specs"
                ? t("functionalities.page.configComparison")
                : selectedModule === "Document Contradiction"
                ? t("functionalities.page.configContradiction")
                : selectedModule === "AI Insight"
                ? t("aiInsight.page.analyseConfigHeading")
                : t("functionalities.page.configUpload")
            }
            middleContent={MiddleContent}
            buttonText={getButtonText()}
            onClick={handleButtonClick}
            disabled={!canStartComparison || isProcessing || isLoading}
            showButton={!!selectedModule}
            showDocuments={!!selectedModule}
            buttonStartIcon={
              isProcessing || isLoading ? (
                selectedModule === "Request Specs" ? (
                <Icon icon="hugeicons:pathfinder-merge" />
              ) : selectedModule === "Document Contradiction" ? (
                <Icon icon="mingcute:alert-triangle-line" />
              ) : selectedModule === "AI Insight" ? (
                <Icon icon="hugeicons:brain" />
              ) : (
                <Icon icon="eva:cloud-upload-fill" />
              )
            ): undefined
            }
          />
        </Grid>
      </Grid>
      

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteConfirmDialog.open}
        title={t("functionalities.page.deleteDialogTitle")}
        message={t("functionalities.page.deleteDialogMessage", {
          name: deleteConfirmDialog.documentToDelete?.tittle,
        })}
        confirmText={t("common.delete")}
        cancelText={t("common.cancel")}
        confirmColor="error"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </Box>
  );
};

export default Functionaliteiten;
